/**
 * 系统管理员登录页（见方案 2B.2）。
 *
 * 为什么单独一个页面、且不放进家长/学生端的任何导航里：
 * 管理入口的设计意图是**不对公网暴露**（deploy/Caddyfile 里的 @admin / @admin_ui
 * 只放行内网与 SSH 隧道来源），所以不把它摆进家长/学生端的导航里做广告。
 *
 * ⚠️ 但"设计意图"不等于"线上事实"。2026-10-16 实测：
 *    从公网打开 https://home.tech21th.top/admin/login 是 **200**，
 *    /api/v1/admin/* 返回 **401**（说明请求到达了应用）——**没有**配置里写的 403。
 *    原因是 caddy 容器挂载的 /etc/caddy/Caddyfile 仍指向**替换前的旧 inode**
 *    （单文件 bind mount 绑的是 inode，不是路径）：只 reload 没用，
 *    必须重建容器才会读到新文件。核查过程与两种处置方案见
 *    docs/RUNBOOK.md §5.4、docs/部署手册.md §10.2/§10.4。
 *
 *    因此本页**不再写任何关于网络来源的说明**：那是运维事实，会漂移，
 *    也不是登录者需要知道的事。登录页只保留对登录者真正有用的信息。
 *    （原来这里还印着 operator.ts create-admin / verify-admin 的用法 ——
 *    那是运维命令，只该待在 docs/RUNBOOK.md §2.4 与 operator.ts --help 里。）
 *
 * 登录成功后的落点由 RouteGuards 决定：
 *  - 已绑定动态口令 → 进 /admin
 *  - 未绑定（理论上被 DB 约束挡住）→ 只会看到绑定页
 */
import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ERROR_CODES, isSystemAdmin } from '@fht/shared';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { CaptchaField } from '../../components/CaptchaField';
import { BuildStamp } from '../../components/layout/BuildStamp';
import { useAuth } from '../../lib/auth';
import * as authApi from '../../lib/api/auth';
import { runAdminLogin } from '../../lib/adminAuth';

export function AdminLoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [captchaId, setCaptchaId] = useState<string | null>(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaNonce, setCaptchaNonce] = useState(0);
  const [failures, setFailures] = useState(() => authApi.getLoginFailures('system_admin', ''));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // 已经以管理员身份登录：直接进后台（避免"能登录却进不去"的死角）
  if (isSystemAdmin(user?.role)) return <Navigate to="/admin" replace />;

  const captchaActive = failures >= 1;

  /**
   * 提交。
   *
   * ⚠️ 2026-10-18 用户实测的问题就在这个函数里，两处都改了：
   *
   *  1. **先校验再发请求**：用户名 / 密码为空 → 直接显示「请填写用户名」「请填写密码」，
   *     `runAdminLogin` 连 `login()` 都不会调用（表单是 `noValidate`，所以不靠浏览器的
   *     `required` 气泡 —— 那个文案不可控，也没法被测试锁定）。
   *  2. **失败文案统一归一**：`runAdminLogin` 内部走 `describeFailureMessage()`
   *     （统一出口，见 `lib/api/failure.ts`），拿不到人话时是「登录失败，请重试」。
   *     **任何情况下都不会把后端响应体 / zod issues 那串 JSON 显示出来。**
   *
   * 既有分支（动态口令 / 验证码 / 锁定 / 密码错）原样保留：`result.code` 与原来
   * `err instanceof ApiError ? err.code : null` 同义。
   */
  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    try {
      const result = await runAdminLogin(
        { username, password, totpCode, captchaActive, captchaId, captchaAnswer, failures },
        login,
        () => setSubmitting(true),
      );

      if (result.kind === 'invalid') {
        setError(result.message);
        return;
      }

      if (result.kind === 'failed') {
        setError(result.message);
        const code = result.code;
        if (code === ERROR_CODES.TOTP_REQUIRED || code === ERROR_CODES.TOTP_INVALID) {
          setNeedTotp(true);
        }
        if (
          code === ERROR_CODES.INVALID_CREDENTIALS ||
          code === ERROR_CODES.CAPTCHA_REQUIRED ||
          code === ERROR_CODES.CAPTCHA_INVALID ||
          code === ERROR_CODES.CAPTCHA_EXPIRED
        ) {
          const next = authApi.recordLoginFailure('system_admin', username);
          setFailures(next);
          setCaptchaAnswer('');
          setCaptchaNonce((n) => n + 1);
        }
        return;
      }

      authApi.clearLoginFailures('system_admin', username);
      navigate(isSystemAdmin(result.me.role) ? '/admin' : '/', { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 px-4 py-10">
      <div className="mx-auto w-full max-w-md">
        <div className="mb-5 text-center text-white">
          <span aria-hidden="true" className="text-4xl">
            🛠
          </span>
          <h1 className="mt-2 text-2xl font-bold">系统管理后台</h1>
          <p className="mt-1 text-sm text-slate-400">
            仅限管理员账号登录 · 不会显示任何家庭数据
          </p>
        </div>

        <div className="rounded-2xl bg-white p-5 shadow-lg sm:p-6">
          {user && (
            <div className="mb-4 rounded-xl bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
              当前浏览器已登录为「{user.nickname}」（{user.role}）。
              以管理员身份登录会替换当前会话的令牌，原会话随时可用原密码重新登录。
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <Input
              label="管理员用户名"
              name="username"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
            />
            <Input
              label="密码"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Input
              label="动态口令"
              name="totpCode"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              hint={
                needTotp
                  ? '必填：打开认证器 App，输入 6 位数字'
                  : '已绑定动态口令的管理员必填（6 位数字）'
              }
            />

            {captchaActive && (
              <div className="space-y-3 rounded-xl bg-amber-50/60 p-3">
                <p className="text-xs text-amber-800">
                  连续登录失败 {failures} 次，请输入图形验证码后重试。
                </p>
                <CaptchaField onChange={setCaptchaId} refreshToken={captchaNonce} />
                <Input
                  label="验证码"
                  name="captchaAnswer"
                  autoComplete="off"
                  maxLength={16}
                  value={captchaAnswer}
                  onChange={(e) => setCaptchaAnswer(e.target.value)}
                />
              </div>
            )}

            {error && (
              <div role="alert" className="rounded-xl bg-rose-50 px-3 py-2.5 text-sm text-rose-700">
                {error}
              </div>
            )}

            <Button type="submit" fullWidth size="lg" loading={submitting}>
              登录管理后台
            </Button>
          </form>
        </div>

        {/*
          「构建于 …」版本标记（2026-10-16 补）。

          为什么登录页也要有：管理后台的入口顺序是"先登录、后进面板"，
          未登录时**这一页就是管理端唯一看得到的页面** —— 只把标记加进
          AdminShell 的话，"强刷后确认构建版本"这件事在登录这一步仍然做不到。
          与面板、家长端是**同一个** `BuildStamp`（见 lib/buildStamp.ts），
          值同源、格式同形；这里只是换个位置（卡片下方居中），不另写一份。
          版本号不含任何账号/家庭数据。
        */}
        <BuildStamp className="mt-6 text-center" />
      </div>
    </div>
  );
}
